import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'compact' | 'hero';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  const formattedDate = new Date(article.publishedAt).toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  if (variant === 'hero') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group block relative overflow-hidden rounded-xl aspect-[16/9] lg:aspect-[21/9] cursor-pointer"
      >
        <img
          src={article.coverImage}
          alt={article.title}
          className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10"></div>
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            {article.isHot && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary-500 text-background-50">
                <i className="ri-fire-line text-xs"></i> 热门
              </span>
            )}
            {article.isNew && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-accent-500 text-background-50">
                <i className="ri-sparkling-line text-xs"></i> 新瓜
              </span>
            )}
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-background-50/20 text-background-50 backdrop-blur-sm">
              {article.category}
            </span>
          </div>
          <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-background-50 mb-2 line-clamp-2 leading-tight group-hover:text-primary-300 transition-colors">
            {article.title}
          </h2>
          <p className="text-sm text-background-200/80 line-clamp-1 mb-3">
            {article.excerpt}
          </p>
          <div className="flex items-center gap-4 text-xs text-background-300/80">
            <span className="flex items-center gap-1">
              <i className="ri-user-line"></i> {article.author}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i> {formattedDate}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i> {formatViews(article.views)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line"></i> {article.comments}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
      >
        <div className="w-24 h-18 flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-500 transition-colors">
            {article.title}
          </h3>
          <div className="flex items-center gap-3 mt-2 text-xs text-foreground-500">
            <span>{formattedDate}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i> {formatViews(article.views)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all duration-300 cursor-pointer"
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute top-3 left-3 flex items-center gap-1.5 flex-wrap">
          {article.isHot && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary-500 text-background-50">
              <i className="ri-fire-line text-xs"></i> 热门
            </span>
          )}
          {article.isNew && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-accent-500 text-background-50">
              <i className="ri-sparkling-line text-xs"></i> 新瓜
            </span>
          )}
        </div>
        <div className="absolute bottom-3 left-3">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-background-950/60 text-background-50 backdrop-blur-sm">
            {article.category}
          </span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-base font-semibold text-foreground-900 line-clamp-2 leading-snug mb-2 group-hover:text-primary-500 transition-colors">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed mb-3">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between text-xs text-foreground-500">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <i className="ri-user-line"></i> {article.author}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i> {formattedDate}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i> {formatViews(article.views)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line"></i> {article.comments}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}